import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CalendarDays, Clock3, Loader2, MapPin, Save } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Confermato", "Completato", "Annullato"];

const STATUS_STYLE = {
  Confermato: "bg-brand-mist text-brand-blue",
  Completato: "bg-brand-aqua/15 text-brand-aquadark",
  Annullato: "bg-red-50 text-red-700",
};

function AppointmentCard({ appt, onUpdated }) {
  const [status, setStatus] = useState(appt.status);
  const [note, setNote] = useState(appt.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/appointments/${appt.id}`, { status, note });
      toast.success("Appuntamento aggiornato");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const dateLabel = new Date(appt.data + "T12:00:00").toLocaleDateString("it-IT", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

  return (
    <article className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid={`admin-appointment-card-${appt.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 font-extrabold text-brand-ink capitalize">
          <CalendarDays className="w-4 h-4 text-brand-blue" /> {dateLabel}
        </span>
        <span className="inline-flex items-center gap-1.5 font-bold text-brand-ink">
          <Clock3 className="w-4 h-4 text-brand-aqua" /> {appt.orario}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-mist text-brand-blue text-xs font-bold px-3 py-1">
          <MapPin className="w-3.5 h-3.5" /> {appt.sede}
        </span>
        <span className={`rounded-full text-xs font-bold px-3 py-1 ${STATUS_STYLE[appt.status] || ""}`}>
          {appt.status}
        </span>
      </div>
      <p className="mt-3 font-bold text-brand-ink">{appt.nome} {appt.cognome}</p>
      <p className="text-sm text-slate-500">{appt.email} · {appt.telefono}</p>
      {appt.nota && <p className="mt-2 text-slate-600 leading-relaxed">{appt.nota}</p>}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input-field h-12 text-base"
          data-testid={`admin-appointment-status-${appt.id}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota interna…"
          className="input-field h-12 text-base"
          data-testid={`admin-appointment-note-${appt.id}`}
        />
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-aqua text-white font-bold px-6 h-12 disabled:opacity-60"
          data-testid={`admin-appointment-save-${appt.id}`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva
        </button>
      </div>
    </article>
  );
}

export default function AdminAppointments() {
  const [items, setItems] = useState(null);

  const load = () => {
    api.get("/admin/appointments").then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(load, []);

  if (items === null) {
    return (
      <div className="flex justify-center py-12" data-testid="admin-appointments-loading">
        <Loader2 className="w-7 h-7 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="rounded-2xl bg-brand-mist p-8 text-center font-semibold text-slate-500" data-testid="admin-appointments-empty">
        Nessun appuntamento prenotato al momento.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-appointments-list">
      {items.map((a) => <AppointmentCard key={a.id} appt={a} onUpdated={load} />)}
    </div>
  );
}
